'use client';

import * as React from 'react';
import { icons, type LucideProps } from 'lucide-react';
import { cn } from '@/lib/utils';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md';

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  leftIcon?: keyof typeof icons;
  rightIcon?: keyof typeof icons;
}

const BASE =
  'inline-flex items-center gap-2 font-medium rounded-sm transition-colors duration-150 whitespace-nowrap focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2 disabled:pointer-events-none disabled:opacity-50';

const SIZE: Record<ButtonSize, string> = {
  sm: 'h-7 px-2.5 text-xs',
  md: 'h-8 px-3.5 text-sm',
};

const VARIANT: Record<ButtonVariant, string> = {
  primary: 'bg-accent text-text-on-accent hover:bg-accent-hover active:bg-accent-active',
  secondary:
    'bg-surface-3 text-text-primary border border-border-default hover:bg-surface-2',
  ghost: 'text-text-secondary hover:text-text-primary hover:bg-surface-2',
  danger: 'text-status-busy border border-border-default hover:bg-surface-2',
};

function renderIcon(name: keyof typeof icons | undefined) {
  if (!name) return null;
  const LucideIcon = icons[name] as React.ComponentType<LucideProps>;
  if (!LucideIcon) return null;
  return (
    <LucideIcon
      width={14}
      height={14}
      strokeWidth={1.75}
      aria-hidden="true"
    />
  );
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      leftIcon,
      rightIcon,
      className,
      children,
      type = 'button',
      ...rest
    },
    ref
  ) => {
    return (
      <button
        ref={ref}
        type={type}
        className={cn(BASE, SIZE[size], VARIANT[variant], className)}
        {...rest}
      >
        {renderIcon(leftIcon)}
        {children}
        {renderIcon(rightIcon)}
      </button>
    );
  }
);
Button.displayName = 'DsButton';
